import { useMemo } from "react";
import { SubmissionAnalyticsRow } from "../../../../client";
import { formatPct } from "./helpers";
import {
  StageCascade,
  CascadeStage,
  CascadeLeaf,
  CascadeCompleted,
} from "./StageCascade";

// Maps submission reports onto the generic StageCascade. Everything specific to
// the underwriting pipeline lives here; the cascade itself knows nothing about
// statuses. Each report is placed once, at the furthest stage it actually
// reached (report.furthest_stage, from the status log) rather than inferred
// from its current status. So a deal declined or lost without ever being quoted
// peels off at the gate it really left. A terminal type can appear under more
// than one gate.

type DispKind = "good" | "stay" | "exit";

// The pipeline spine, by rank. `stub` is the current-status bucket of reports
// sitting open at that stage.
const STAGES: { id: string; label: string; color: string; stub: string }[] = [
  {
    id: "submitted",
    label: "Submitted",
    color: "#5a97f5",
    stub: "pre_clearance",
  },
  { id: "cleared", label: "Cleared", color: "#2eb6c4", stub: "cleared_open" },
  {
    id: "in_progress",
    label: "In progress",
    color: "#36a3c9",
    stub: "in_progress_open",
  },
  {
    id: "indicated",
    label: "Indicated",
    color: "#6a8fe0",
    stub: "indicated_open",
  },
  { id: "quoted", label: "Quoted", color: "#a384f5", stub: "quoted_open" },
];
const QUOTED_RANK = STAGES.length - 1; // 4
const BOUND_RANK = STAGES.length; // 5: the win, past the final gate

const OTHER = { label: "Other", color: "#b8bfca", kind: "exit" as DispKind };

// Leaf catalog. `good` = the win (Bound). `stay` = open at a stage. `exit` =
// terminal exit. Labels come from backend statuses.json.
const DISPOSITIONS: Record<
  string,
  { label: string; color: string; kind: DispKind }
> = {
  bound: { label: "Bound", color: "#52c478", kind: "good" },
  pre_clearance: { label: "Pre-clearance", color: "#5a97f5", kind: "stay" },
  cleared_open: {
    label: "Cleared, awaiting review",
    color: "#2eb6c4",
    kind: "stay",
  },
  in_progress_open: { label: "In progress", color: "#36a3c9", kind: "stay" },
  indicated_open: { label: "Indicated", color: "#6a8fe0", kind: "stay" },
  quoted_open: { label: "Quoted, open", color: "#a384f5", kind: "stay" },
  reviewed_not_quoted: {
    label: "Reviewed, not quoted",
    color: "#b08968",
    kind: "exit",
  },
  declined: { label: "Declined", color: "#c2566b", kind: "exit" },
  lost: { label: "Not taken up", color: "#e8743b", kind: "exit" },
  not_reviewed: { label: "Not reviewed", color: "#8a94a6", kind: "exit" },
  not_cleared: { label: "Not cleared", color: "#9c4f3c", kind: "exit" },
  other: OTHER,
};
const dispOf = (id: string): { label: string; color: string; kind: DispKind } =>
  DISPOSITIONS[id] ?? OTHER;

// Current status → leaf disposition.
const STATUS_TO_DISP: Record<string, string> = {
  bound: "bound",
  issued: "bound",
  quoted: "quoted_open",
  pending: "in_progress_open",
  modeling_complete: "in_progress_open",
  indicated: "indicated_open",
  cleared: "cleared_open",
  pre_underwriting_review: "pre_clearance",
  blocked: "pre_clearance",
  not_reviewed: "not_reviewed",
  closed: "reviewed_not_quoted",
  closed_bor: "other",
  lost: "lost",
  quoted_not_taken_up: "lost",
  declined: "declined",
  not_cleared_prior_submission: "not_cleared",
  not_cleared_incomplete_submission: "not_cleared",
  not_cleared_out_of_appetite: "not_cleared",
  not_cleared_effective_date_too_far: "not_cleared",
  not_cleared_unapproved_broker: "not_cleared",
  not_cleared_low_tiv: "not_cleared",
  not_cleared_foreign_exposure: "not_cleared",
};

// Rank of each stub's stage (pre_clearance=0 … quoted_open=4).
const STUB_RANK: Record<string, number> = {};
STAGES.forEach((s, i) => {
  STUB_RANK[s.stub] = i;
});

// Order of exits top-to-bottom within a gate's bottom section.
const BAD_ORDER = [
  "reviewed_not_quoted",
  "declined",
  "lost",
  "not_reviewed",
  "not_cleared",
  "other",
];

// The stage a report leaves the spine at: bound advances to the win; an open
// report sits at its current stage; a terminal exits at the furthest stage it
// actually reached (capped below the win).
const classify = (
  r: SubmissionAnalyticsRow
): { disp: string; rank: number } => {
  const disp = STATUS_TO_DISP[r.status] ?? "other";
  const kind = dispOf(disp).kind;
  let rank: number;
  if (disp === "bound") rank = BOUND_RANK;
  else if (kind === "stay") rank = STUB_RANK[disp] ?? 0;
  else rank = Math.max(0, Math.min(QUOTED_RANK, r.furthest_stage));
  return { disp, rank };
};

const toCascade = (
  reports: SubmissionAnalyticsRow[]
): {
  stages: CascadeStage[];
  leaves: CascadeLeaf[];
  completed: CascadeCompleted;
} => {
  const cell: Record<string, number> = {};
  let boundCount = 0;
  for (const r of reports) {
    const { disp, rank } = classify(r);
    if (disp === "bound") {
      boundCount += 1;
      continue;
    }
    cell[`${rank}|${disp}`] = (cell[`${rank}|${disp}`] ?? 0) + 1;
  }

  const leaves: CascadeLeaf[] = [];
  for (let g = 0; g <= QUOTED_RANK; g++) {
    const stage = STAGES[g];
    if (!stage) continue;
    const addLeaf = (disp: string, kind: "stay" | "exit"): void => {
      const v = cell[`${g}|${disp}`] ?? 0;
      if (v <= 0) return;
      const d = dispOf(disp);
      leaves.push({
        stageId: stage.id,
        kind,
        label: d.label,
        color: d.color,
        value: v,
      });
    };
    addLeaf(stage.stub, "stay");
    for (const bd of BAD_ORDER) addLeaf(bd, "exit");
  }

  const bound = dispOf("bound");
  return {
    stages: STAGES.map((s) => ({ id: s.id, label: s.label, color: s.color })),
    leaves,
    completed: { label: bound.label, color: bound.color, value: boundCount },
  };
};

export const StatusFlow = ({
  reports,
}: {
  reports: SubmissionAnalyticsRow[];
}) => {
  const { stages, leaves, completed } = useMemo(
    () => toCascade(reports),
    [reports]
  );
  return (
    <StageCascade
      stages={stages}
      leaves={leaves}
      completed={completed}
      formatPct={(f) => formatPct(f * 100)}
      emptyMessage="No submissions in the selected window."
    />
  );
};
